import moment from "moment";
import {
  first,
  fromTemplateRowApi,
  label,
  listOf,
  personName,
  query,
  request,
  toBool,
  toDay,
  toNumber,
  unwrap,
  versionNumber,
} from "../SalaryTemplates/salaryTemplateApi";
import {
  arrayOf,
  assignRequestAction,
  customComponentRows,
  findRows,
  fromHistoryApi,
  idText,
  objectOf,
  personFields,
  cardsOf,
  readCardCounts,
  readStatus,
  toServerId,
  uniqueBy,
} from "../AssignSalary/assignSalaryApi";

// Payroll > Salary > Salary Revision on the payroll/salary/ APIs (spec
// section 7): salary-revision (list / single / create / edit / delete
// draft), revision-eligible-employees, get-salary-revision-choices and
// salary-request-action (submit / approve / reject / cancel, shared with
// Assign Salary).
//
// The API doc has no sample responses, so every reader below accepts the
// likely field names and falls back to safe defaults. Check them against a
// live response.

const BASE = "payroll/salary/";
const REVISION = `${BASE}salary-revision/`;
const LEVEL_ROLES = { 1: "HR Manager", 2: "Finance Manager" };

// Screen value <-> API value.
const METHOD_TO_API = { percent: "percentage", fixed: "fixed", set: "set", none: "none" };
const STRUCTURE_TO_API = { keep: "keep", change: "template", customize: "customize" };

const readMethod = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.startsWith("percent") || text === "%") return "percent";
  if (text.startsWith("fixed")) return "fixed";
  if (text.startsWith("set")) return "set";
  return "none";
};
const readStructure = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.startsWith("template") || text.startsWith("change")) return "change";
  if (text.startsWith("custom")) return "customize";
  return "keep";
};
const readKind = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("template")) return "template";
  if (text.includes("import")) return "import";
  return "employees";
};

// Rows (with amounts when the server sent them) for one side of the
// comparison: current or revised.
const sideRows = (...values) => {
  const rows = values.find((value) => Array.isArray(value) && value.length);
  // In a current / revised row, amount is the worked-out amount.
  return rows
    ? rows.map((row) => {
        const line = fromTemplateRowApi(row);
        return { ...line, server_amount: line.server_amount ?? toNumber(first(row.amount, row.monthly_amount, row.computed_value)) };
      })
    : null;
};

// Server amounts for a side, in the shape the breakdown reads, or null.
const sideResult = (lines, gross, totals = {}) => {
  if (!lines?.length || !lines.some((line) => line.server_amount != null)) return null;
  const values = Object.fromEntries(lines.map((line) => [line.code, line.server_amount]));
  const sum = (category) => lines.filter((line) => line.category === category).reduce((total, line) => total + (line.server_amount || 0), 0);
  const earnings = toNumber(first(totals.total_earnings, totals.earnings_total)) ?? sum("Earnings");
  const deductions = toNumber(first(totals.total_deductions, totals.deductions_total)) ?? sum("Deductions");
  return {
    values,
    earnings: earnings || toNumber(gross) || 0,
    deductions,
    net: toNumber(first(totals.net_before_statutory, totals.net)) ?? earnings - deductions,
    balanced: true,
    unresolved: [],
    errors: [],
  };
};

// ---------- Employees ----------

// An employee as the picker and the form rows use it.
export const fromEligibleApi = (row) => {
  const salary = objectOf(first(row.current_salary, row.salary));
  const template = objectOf(first(row.template, salary.template));
  const lines = sideRows(row.components, row.rows, salary.components, salary.rows);
  const structureText = String(first(row.structure_type, row.structure, salary.structure_type) || "");
  return {
    ...personFields(row),
    group_id: idText(first(row.payroll_group_id, objectOf(row.payroll_group).id)),
    salary_type: first(row.salary_type, row.pay_basis, salary.salary_type, template.pay_basis) || "",
    template_id: idText(first(row.template_id, row.current_template_id, salary.template_id, template.id)),
    template_name: first(row.template_name, row.current_template_name, salary.template_name, template.name) || "",
    version: versionNumber(first(row.template_version, row.template_version_no, row.current_template_version, row.version, salary.template_version, template.version)),
    version_id: first(row.template_version_id, row.current_template_version_id, salary.template_version_id) ?? null,
    gross: toNumber(first(row.gross, row.current_gross, row.gross_salary, salary.gross)),
    customized: toBool(first(row.is_customized, row.customized, salary.is_customized), /custom/i.test(structureText)),
    // Own rows only mark a Customized employee; everyone's rows still feed
    // the preview.
    lines: toBool(first(row.is_customized, row.customized, salary.is_customized), /custom/i.test(structureText)) ? lines : null,
    current_lines: lines,
    eligible: toBool(first(row.eligible, row.is_eligible), true),
    reason: first(row.reason, row.ineligible_reason, row.not_eligible_reason, row.message) || "",
    last_revised: toDay(first(row.last_revised, row.last_revised_on, row.last_revision_date)),
  };
};

const isEmployeeRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  const fields = personFields(row);
  return Boolean(fields.id && fields.name);
};

// Employee picker. With templateId: everyone on that template (Template
// Linked come pre-ticked, Customized flagged).
export const listEligibleEmployees = async (domain, companyId, { templateId } = {}, signal) => {
  const data = await request(domain, query(`${BASE}revision-eligible-employees/`, { company_id: companyId, template_id: templateId }), { signal });
  const value = unwrap(data);
  const list = listOf(data, ["employees", "eligible_employees", "users"]) || findRows(value, isEmployeeRow);
  return uniqueBy(list.filter(isEmployeeRow).map(fromEligibleApi), (row) => row.id);
};

// ---------- Add employees: group -> template -> salary type -> users ----------
// The Select Employees panel loads its list in steps: the company's payroll
// groups, that group's templates (payroll-group-templates), the template's
// salary type (template-salary-type), then everyone on that salary type
// (salary-type-users). No sample responses yet, so the readers accept the
// likely field names.

const PAYROLL_GROUPS = "payroll/setup/payrollgroup/payroll-group/";

export const listPayrollGroups = async (domain, companyId, signal) => {
  const data = await request(domain, query(PAYROLL_GROUPS, { company: companyId }), { signal });
  return uniqueBy(
    (listOf(data, ["payroll_groups", "groups"]) || [])
      .map((item) => ({
        id: idText(first(item?.id, item?.payroll_group_id, item?.value)),
        name: first(item?.name, item?.payroll_group_name, item?.group_name, item?.label) || "",
      }))
      .filter((item) => item.id && item.name),
    (item) => item.id,
  );
};

export const listGroupTemplates = async (domain, payrollGroupId, signal) => {
  const data = await request(domain, query(`${BASE}payroll-group-templates/`, { payroll_group_id: payrollGroupId }), { signal });
  return uniqueBy(
    (listOf(data, ["templates", "template_choices", "salary_templates"]) || [])
      .map((item) => ({
        id: idText(first(item?.id, item?.template_id, item?.value)),
        name: first(item?.name, item?.template_name, item?.label) || "",
        salary_type: first(item?.salary_type, item?.pay_basis) || "",
      }))
      .filter((item) => item.id && item.name),
    (item) => item.id,
  );
};

export const getTemplateSalaryType = async (domain, templateId, signal) => {
  const data = unwrap(await request(domain, query(`${BASE}template-salary-type/`, { template_id: templateId }), { signal }));
  const value = Array.isArray(data) ? data[0] : data;
  if (typeof value === "string") return value;
  const type = first(value?.salary_type, value?.pay_basis, value?.salary_type_name, objectOf(value?.template).salary_type, objectOf(value?.template).pay_basis);
  return typeof type === "string" ? type : label(type) || "";
};

export const listSalaryTypeUsers = async (domain, companyId, salaryType, signal) => {
  const data = await request(domain, query(`${BASE}salary-type-users/`, { company_id: companyId, salary_type: salaryType }), { signal });
  const value = unwrap(data);
  const list = listOf(data, ["users", "employees"]) || findRows(value, isEmployeeRow);
  return uniqueBy(list.filter(isEmployeeRow).map(fromEligibleApi), (row) => row.id);
};

// ---------- Revisions ----------

const fromRevisionEmployee = (row) => {
  const current = objectOf(first(row.current, row.current_salary, row.from));
  const revised = objectOf(first(row.revised, row.new_salary, row.proposed, row.to));
  const currentLines = sideRows(row.current_rows, row.current_components, current.rows, current.components);
  const revisedLines = sideRows(row.revised_rows, row.new_rows, row.revised_components, row.new_components, revised.rows, revised.components, row.components);
  const currentGross = toNumber(first(row.current_gross, row.old_gross, current.gross, row.gross));
  const newGross = toNumber(first(row.new_gross, row.revised_gross, row.proposed_gross, revised.gross)) ?? currentGross;
  const customized = toBool(
    first(row.is_customized, row.current_customized, current.is_customized, current.customized),
    /custom/i.test(String(first(row.structure_type, current.structure_type) || "")),
  );
  return {
    ...personFields(row),
    employee_id: personFields(row).id,
    current_gross: currentGross ?? 0,
    new_gross: newGross ?? 0,
    effective_from: toDay(first(row.effective_from, row.effective_date)),
    from: {
      template_id: idText(first(row.current_template_id, row.from_template_id, current.template_id)),
      template_name: first(row.current_template_name, row.from_template_name, current.template_name) || "",
      version: versionNumber(first(row.current_template_version, row.from_version, current.template_version, current.version)),
      customized,
    },
    to: {
      template_id: idText(first(row.new_template_id, row.to_template_id, revised.template_id)),
      template_name: first(row.new_template_name, row.to_template_name, revised.template_name) || "",
      version: versionNumber(first(row.new_template_version, row.to_version, revised.template_version, revised.version)),
    },
    lines: toBool(first(row.new_customized, revised.is_customized), false) ? revisedLines : null,
    current_lines: currentLines,
    revised_lines: revisedLines,
    current_result: sideResult(currentLines, currentGross, { ...current, ...objectOf(row.current_totals) }),
    revised_result: sideResult(revisedLines, newGross, { ...revised, ...objectOf(row.totals) }),
  };
};

// List row / single -> the revision object the screen uses.
export const fromRevisionApi = (raw) => {
  const row = { ...objectOf(raw.summary), ...raw };
  const { status, level, upcoming } = readStatus(first(row.status, row.request_status, row.status_name, row.status_label), row);
  const employees = arrayOf(first(row.employees, row.employee_rows, row.employee_details, row.details))
    .filter((item) => item && typeof item === "object")
    .map(fromRevisionEmployee);
  const history = arrayOf(first(row.history, row.approval_history, row.logs, row.timeline, row.activity))
    .map(fromHistoryApi)
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const rejectEntry = history.find((entry) => entry.title.startsWith("Rejected"));
  const rejection = objectOf(row.rejection);
  const rejectionLevel = toNumber(first(rejection.level, row.rejected_level, rejectEntry?.level)) ?? level;
  const template = objectOf(row.template);
  const id = idText(first(row.request_id, row.id));
  const kind = readKind(first(row.revision_type, row.type, row.kind));
  const names = employees.length
    ? employees.map((item) => item.name).filter(Boolean)
    : (Array.isArray(row.employee_names) ? row.employee_names : String(row.employee_names || "").split(","))
        .map((name) => String(typeof name === "string" ? name : personFields(objectOf(name)).name).trim())
        .filter(Boolean);
  return {
    id,
    number: first(row.request_no, row.request_number, row.number, row.reference_no, row.request_code) || (id ? `#${id}` : ""),
    kind,
    revision_type: first(row.revision_type, row.type) || "",
    title: first(row.title, row.file_name) || "",
    reason: first(row.reason, row.revision_reason) || "",
    comment: first(row.comments, row.comment) || "",
    structure: kind === "template" ? "change" : readStructure(first(row.structure_choice, row.structure)),
    change: { mode: readMethod(first(row.salary_method, row.method, row.change_mode)), value: String(first(row.method_value, row.change_value, row.value) ?? "") },
    template_id: idText(first(row.template_id, row.new_template_id, template.id)),
    template_name: first(row.template_name, row.new_template_name, template.name) || "",
    target_version: versionNumber(first(row.template_version, row.new_template_version, row.target_version, template.version, template.version_no)),
    target_version_id: first(row.template_version_id, row.new_template_version_id) ?? null,
    source_template_id: idText(row.source_template_id),
    source_version: versionNumber(first(row.source_template_version, row.source_version)),
    source_version_id: row.source_template_version_id ?? null,
    salary_type: first(row.salary_type, row.pay_basis, template.pay_basis) || "",
    effective_from: toDay(first(row.effective_from, row.effective_date)),
    employees,
    employee_count: toNumber(first(row.employee_count, row.employees_count, row.total_employees, row.no_of_employees)) ?? employees.length,
    employee_names: names,
    first_employee: employees[0] || null,
    total_current: toNumber(first(row.total_current_gross, row.current_total, row.current_gross_total, row.old_gross_total)),
    total_new: toNumber(first(row.total_new_gross, row.new_total, row.new_gross_total, row.revised_gross_total)),
    status,
    level,
    upcoming,
    submitted_at: first(row.submitted_at, row.submitted_on) || "",
    approved_at: first(row.approved_at, row.approved_on) || "",
    imported_at: first(row.imported_at, row.imported_on) || "",
    saved_at: first(row.updated_at, row.modified_at, row.updated_on, row.created_at, row.created_on) || "",
    saved_by: personName(first(row.updated_by_name, row.updated_by, row.created_by_name, row.created_by, row.submitted_by)),
    created_by: personName(first(row.created_by_name, row.created_by, row.raised_by_name, row.raised_by, row.submitted_by)),
    rejection:
      status === "Rejected"
        ? {
            level: rejectionLevel,
            role: LEVEL_ROLES[rejectionLevel] || "Approver",
            by: personName(first(rejection.by, row.rejected_by_name, row.rejected_by)) || rejectEntry?.by || "the approver",
            at: first(rejection.at, row.rejected_at, row.rejected_on) || rejectEntry?.at || "",
            reason: first(rejection.reason, row.rejection_reason, row.reject_reason, row.remarks) || rejectEntry?.note || "",
          }
        : null,
    history,
    can_approve: toBool(first(row.can_approve, row.is_approver), undefined),
    pending_mine: status === "Pending" && toBool(first(row.pending_my_approval, row.is_pending_my_approval, row.my_approval, row.can_approve), false),
    can_cancel: toBool(row.can_cancel, undefined),
    can_edit: toBool(row.can_edit, undefined),
    details_loaded: false,
  };
};

const isRevisionRow = (row) =>
  row && typeof row === "object" && !Array.isArray(row) &&
  first(row.request_id, row.id) != null &&
  first(row.request_id, row.request_no, row.request_number, row.revision_type, row.salary_method, row.structure_choice) != null;

// List: rows + card counts (All, Draft, Approved, Rejected).
export const listRevisions = async (domain, companyId, signal) => {
  const data = await request(domain, query(REVISION, { company_id: companyId }), { signal });
  if (!data || typeof data !== "object") throw new Error("The server returned an invalid salary revision list.");
  const value = unwrap(data);
  const list = listOf(data, ["requests", "revisions", "salary_revisions"]) || findRows(value, isRevisionRow);
  const counts = readCardCounts(cardsOf(data));
  const pick = (...keys) => keys.map((key) => counts[key]).find((count) => count != null) ?? null;
  return {
    revisions: uniqueBy(list.filter(isRevisionRow), (row) => idText(first(row.request_id, row.id))).map(fromRevisionApi),
    counts: {
      all: pick("all", "total"),
      Draft: pick("draft", "drafts"),
      Approved: pick("approved"),
      Rejected: pick("rejected"),
    },
  };
};

// One revision: summary, approval chain, history, current vs revised rows.
export const getRevision = async (domain, id, signal) => {
  const data = unwrap(await request(domain, query(REVISION, { request_id: id }), { signal }));
  const row = Array.isArray(data) ? data[0] : first(data?.request, data?.revision) || data;
  if (!row || typeof row !== "object") throw new Error("The server returned an invalid salary revision.");
  return { ...fromRevisionApi(row), details_loaded: true };
};

// Reasons, and the first date a revision can start (when the server says).
export const getRevisionChoices = async (domain, signal) => {
  const data = unwrap(await request(domain, `${BASE}get-salary-revision-choices/`, { signal }));
  const labels = (value) => (Array.isArray(value) ? value.map(label).filter(Boolean) : null);
  const lastProcessed = toDay(first(data?.last_processed_payroll, data?.last_processed_date, data?.last_processed_payroll_date, data?.last_processed));
  const firstOpen = toDay(first(data?.first_unprocessed_payroll, data?.first_unprocessed_date, data?.min_effective_from, data?.earliest_effective_from));
  return {
    reasons: labels(first(data?.reason_choices, data?.reasons, data?.revision_reasons)),
    minDate: firstOpen || (lastProcessed ? moment(lastProcessed).add(1, "day").format("YYYY-MM-DD") : ""),
    lastProcessed,
  };
};

// ---------- Save / actions ----------

// The form -> the POST / PUT body.
// ctx: companyId, rows (the employees taking part, with newGross),
// targetVersionId, sourceVersionIds, baseLines (template rows for
// customize), submit.
export const toRevisionBody = (form, ctx) => {
  const isTemplate = form.kind === "template";
  const structure = isTemplate ? "template" : STRUCTURE_TO_API[form.structure] || "keep";
  const method = METHOD_TO_API[form.change_mode] || "none";
  const body = {
    ...(form.revision_id ? { request_id: toServerId(form.revision_id) } : {}),
    company_id: Number(ctx.companyId),
    revision_type: isTemplate ? "Template-Based" : ctx.rows.length === 1 ? "Individual" : "Bulk",
    employees: ctx.rows.map((row) => ({
      user_id: toServerId(row.employee_id),
      // A typed-in gross, every gross for Set new salary, or the gross an
      // employee's own components add up to.
      ...(row.manual || method === "set" || row.components ? { new_gross: Number(row.newGross) } : {}),
      // Per-employee components, same shape as Assign Salary's customized
      // employees. Not in the revision spec yet: backend to confirm.
      ...(row.components ? { components: row.components } : {}),
    })),
    structure_choice: structure,
    salary_method: method,
    ...(method === "percentage" || method === "fixed" ? { method_value: Number(form.change_value) } : {}),
    effective_from: form.effective_from,
    reason: form.reason,
    comments: String(form.comment || "").trim(),
    submit: Boolean(ctx.submit),
  };
  if (isTemplate) {
    body.source_template_id = toServerId(form.template_id);
    const sources = (ctx.sourceVersionIds || []).filter((value) => value != null).map(toServerId);
    if (sources.length) body.source_template_version_id = sources[0];
    if (sources.length > 1) body.source_template_version_ids = sources;
  }
  if (structure !== "keep") {
    body.template_id = toServerId(form.template_id);
    if (ctx.targetVersionId != null) body.template_version_id = toServerId(ctx.targetVersionId);
  }
  if (structure === "customize" && form.common_lines) body.components = customComponentRows(ctx.baseLines || [], form.common_lines);
  return body;
};

// POST (new) or PUT (request_id set; submit: true resubmits).
export const saveRevision = async (domain, body, signal) => {
  const data = unwrap(await request(domain, REVISION, { method: body.request_id ? "PUT" : "POST", body, signal }));
  return idText(first(data?.request_id, data?.id, data?.request?.id, data?.revision?.id)) ?? idText(body.request_id);
};

// Soft delete of a Draft, by the person who created it.
export const deleteRevisionDraft = (domain, id, signal) =>
  request(domain, REVISION, { method: "PUT", body: { request_id: toServerId(id), is_deleted: true }, signal });

export const revisionAction = assignRequestAction;
